import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

interface CarouselImage {
  src: string;
  alt: string;
  title?: string;
  description?: string;
}

interface ImageCarouselProps {
  images: CarouselImage[];
  autoRotate?: boolean;
  rotationInterval?: number;
  showControls?: boolean;
  showDots?: boolean;
  height?: string;
}

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: var(--border-radius-md);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
`;

const CarouselImageContainer = styled.div<{ height: string }>`
  position: relative;
  width: 100%;
  height: ${props => props.height};
  overflow: hidden;
`;

const CarouselImg = styled.img<{ $active: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${props => props.$active ? 1 : 0};
  transition: opacity 0.6s ease-in-out;
`;

const NavigationButton = styled.button<{ direction: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${props => props.direction}: 15px;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.9);
  color: var(--primary-color);
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 3;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background: var(--accent);
    color: white;
    transform: translateY(-50%) scale(1.1);
  }
`;

const CarouselControls = styled.div`
  position: absolute;
  bottom: 15px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 3;
`;

const CarouselDot = styled.button<{ $active: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$active ? 'var(--accent)' : 'rgba(255, 255, 255, 0.6)'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: var(--accent);
    transform: scale(1.2);
  }
`;

const ImageOverlay = styled.div<{ $active: boolean; $hasOverlay: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  color: white;
  padding: 20px 15px 15px;
  transform: translateY(${props => props.$active && props.$hasOverlay ? '0' : '100%'});
  transition: transform 0.6s ease;
  z-index: 2;
`;

const OverlayTitle = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 5px;
  font-weight: 600;
`;

const OverlayDescription = styled.p`
  font-size: 0.85rem;
  opacity: 0.9;
  line-height: 1.3;
`;

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images,
  autoRotate = true,
  rotationInterval = 5000,
  showControls = true,
  showDots = true,
  height = '350px'
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToImage = (index: number) => {
    setCurrentImageIndex(index);
  };

  useEffect(() => {
    if (autoRotate && images.length > 1) {
      const interval = setInterval(nextImage, rotationInterval);
      return () => clearInterval(interval);
    }
  }, [autoRotate, rotationInterval, images.length]);

  if (!images.length) return null;

  const hasOverlay = images.some(img => img.title || img.description);

  return (
    <CarouselContainer>
      <CarouselImageContainer height={height}>
        {images.map((image, index) => (
          <React.Fragment key={index}>
            <CarouselImg
              src={image.src}
              alt={image.alt}
              $active={index === currentImageIndex}
            />
            {hasOverlay && (image.title || image.description) && (
              <ImageOverlay $active={index === currentImageIndex} $hasOverlay={hasOverlay}>
                {image.title && <OverlayTitle>{image.title}</OverlayTitle>}
                {image.description && <OverlayDescription>{image.description}</OverlayDescription>}
              </ImageOverlay>
            )}
          </React.Fragment>
        ))}
      </CarouselImageContainer>
      
      {showControls && images.length > 1 && (
        <>
          <NavigationButton direction="left" onClick={prevImage}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </NavigationButton>
          
          <NavigationButton direction="right" onClick={nextImage}>
            <FontAwesomeIcon icon={faChevronRight} />
          </NavigationButton>
        </>
      )}
      
      {showDots && images.length > 1 && (
        <CarouselControls>
          {images.map((_, index) => (
            <CarouselDot
              key={index}
              $active={index === currentImageIndex}
              onClick={() => goToImage(index)}
            />
          ))}
        </CarouselControls>
      )}
    </CarouselContainer>
  );
};

export default ImageCarousel;
